import { queryOptions, skipToken } from '@tanstack/react-query';

import { fetchPlantProfile } from '../../../plants';
import { fetchMyFeed } from '../api/feed.api';
import { fetchLanding, isLandingGone } from '../api/landing.api';
import { fetchPlantPolicy } from '../api/policy.api';
import { fetchPlantRouting } from '../api/routing.api';
import { fetchMySettings } from '../api/settings.api';
import type { LandingRecord, QuietState } from '../types/domain.types';

import { CTL_GC, CTL_STALE, ctlKeys } from './query-keys';

const QUIET_TURN_SLACK_MS = 5_000;

export const untilQuietTurns = (quiet: Pick<QuietState, 'active' | 'until' | 'startsAt'> | undefined, now: number): number | false => {
  const next = quiet?.active ? quiet.until : quiet?.startsAt;
  if (!next) return false;
  return Math.max(Date.parse(next) - now, 0) + QUIET_TURN_SLACK_MS;
};

export const mySettingsQueryOptions = () =>
  queryOptions({
    queryKey: ctlKeys.settings.me(),
    queryFn: ({ signal }) => fetchMySettings(signal),
    staleTime: CTL_STALE.settings,
    gcTime: CTL_GC,
    refetchInterval: (query) => untilQuietTurns(query.state.data?.quiet, Date.now()),
  });

export const myFeedQueryOptions = () =>
  queryOptions({
    queryKey: ctlKeys.feed.me(),
    queryFn: ({ signal }) => fetchMyFeed(signal),
    staleTime: CTL_STALE.feed,
    gcTime: CTL_GC,
    refetchInterval: (query) => untilQuietTurns(query.state.data?.quiet, Date.now()),
  });

const readLandingOrGone = async (token: string, signal: AbortSignal): Promise<LandingRecord | null> => {
  try {
    return await fetchLanding(token, signal);
  } catch (error) {
    if (isLandingGone(error)) return null;
    throw error;
  }
};

export const landingQueryOptions = (token: string) =>
  queryOptions({
    queryKey: ctlKeys.landing(token),
    queryFn: ({ signal }) => readLandingOrGone(token, signal),
    staleTime: CTL_STALE.landing,
    gcTime: CTL_GC,
  });

export const plantPolicyQueryOptions = (plantId: string | undefined, canView: boolean) =>
  queryOptions({
    queryKey: ctlKeys.policy(plantId ?? ''),
    queryFn: plantId && canView ? ({ signal }) => fetchPlantPolicy(plantId, signal) : skipToken,
    staleTime: CTL_STALE.policy,
    gcTime: CTL_GC,
  });

export const plantRoutingQueryOptions = (plantId: string | undefined, canView: boolean) =>
  queryOptions({
    queryKey: ctlKeys.routing(plantId ?? ''),
    queryFn: plantId && canView ? ({ signal }) => fetchPlantRouting(plantId, signal) : skipToken,
    staleTime: CTL_STALE.routing,
    gcTime: CTL_GC,
  });

export const plantProfileQueryOptions = (plantId: string | undefined, canView: boolean) =>
  queryOptions({
    queryKey: ctlKeys.plantProfile(plantId ?? ''),
    queryFn: plantId && canView ? ({ signal }) => fetchPlantProfile(plantId, signal) : skipToken,
    staleTime: CTL_STALE.plantProfile,
    gcTime: CTL_GC,
  });
